import { isBrowser } from '../utils/generic'
import { useEffect, useRef } from 'react'

// Global singleton polling manager to prevent multiple instances during Gatsby hydration
class PollingManager {
  constructor() {
    this.activeInterval = null
    this.activeCallback = null
    this.isActive = false
    this.mountCount = 0
  }

  startPolling(callback, interval) {
    this.mountCount++

    this.activeCallback = callback

    if (this.isActive) {
      return
    }

    const executeCallback = async () => {
      try {
        await this.activeCallback()
      } catch (error) {
        console.error('Polling callback error:', error)
      }
    }

    executeCallback()

    this.activeInterval = setInterval(executeCallback, interval)
    this.isActive = true
  }

  stopPolling() {
    this.mountCount = Math.max(0, this.mountCount - 1)

    if (this.mountCount === 0 && this.activeInterval) {
      clearInterval(this.activeInterval)
      this.activeInterval = null
      this.activeCallback = null
      this.isActive = false
    }
  }
}

const pollingManager = new PollingManager()

export const usePolling = (isEnabled, callback, interval) => {
  const callbackRef = useRef(callback)
  const hasStartedRef = useRef(false)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    const shouldStartPolling = isBrowser() && isEnabled && !hasStartedRef.current
    const shouldUpdateCallback = isBrowser() && isEnabled && hasStartedRef.current

    if (shouldStartPolling) {
      hasStartedRef.current = true
      pollingManager.startPolling(
        () => callbackRef.current(),
        interval
      )
      return () => {
        hasStartedRef.current = false
        pollingManager.stopPolling()
      }
    }

    if (shouldUpdateCallback) {
      pollingManager.activeCallback = () => callbackRef.current()
    }

    return () => {
      hasStartedRef.current = false
      pollingManager.stopPolling()
    }
  }, [isEnabled, interval])
}
